import $ from 'jquery';
import _ from 'underscore';
import placeholderTemplate from '../template/placeholder.pug';

// Detects if a grid is empty and displays placeholder text
// Supports additional custom placeholders based on if a filter is applied or via an async callback
export const placeholderPlugin = definePlugin => definePlugin(
  'placeholder',
  ['gridView', 'config'],
  (gridView, { plugins: { placeholder: placeholderConfig } }) => {
    let container;
    let filteredContainer;

    function hide(element) {
      if (!_.isUndefined(element) && !_.isNull(element)) {
        element.hide();
      }
    }

    function ensureContainers() {
    // if viewRoot is undefined, jQuery will default to searching the DOM
    // if containers can't be found, they will be re-tried on the next grid update
      const { viewRoot } = placeholderConfig;

      container = $(placeholderConfig.container, viewRoot);

      // filteredContainer is supported for legacy reasons
      // it's recommended to just re-use the normal container
      if (placeholderConfig.filteredContainer) {
        filteredContainer = $(placeholderConfig.filteredContainer, viewRoot);
      }
    }

    function applyTemplate({
      options,
      element = container,
    }) {
      let template = placeholderTemplate;
      if (_.isFunction(placeholderConfig.template)) {
        ({ template } = placeholderConfig);
      }

      // placeholderText will contain the most relevant string to use
      const placeholderText = options.asyncEmptyText || options.filteredEmptyText || options.text;

      if (!_.isUndefined(element) && !_.isNull(element)) {
        element.html(template({
          options,
          placeholderText,
        }));
        element.attr('role', 'alert');
        element.show();
      }
    }

    function isIgnoredFilter(filter) {
      const { ignoreFilters } = placeholderConfig;
      const jsonStrOfFilter = JSON.stringify(filter);
      return !_.isUndefined(_.find(ignoreFilters, obj => jsonStrOfFilter === JSON.stringify(obj)));
    }

    let error = null;

    gridView.on('didReload', (status, data) => {
      error = status ? null : data;
    });

    gridView.on('didUpdate', () => {
      if (gridView._isRendered) {
      // Only fetch the containers once the grid has been rendered
        ensureContainers();

        if (gridView.countRows === 0) {
          const {
            text,
            filteredEmptyText,
            asyncEmptyText,
            ignoreFilters,
            templateOptions,
            getErrorMessage,
            filterService,
          } = placeholderConfig;
          const options = {
            text: error && _.isFunction(getErrorMessage) ? getErrorMessage(error) : text,
            templateOptions,
          };

          let shouldShowFilteredPlaceholder = false;
          if (filteredEmptyText) {
            const gridFilter = filterService
              ? _.result(filterService.getCombinedFilter(), 'query')
              : _.result(gridView.get('query'), 'filter');
            if (!_.isEmpty(gridFilter)) {
              if (ignoreFilters) {
                shouldShowFilteredPlaceholder = _.has(gridFilter, '$and') ?
                  !_.isEmpty(_.reject(gridFilter.$and, isIgnoredFilter)) :
                  !isIgnoredFilter(gridFilter);
              } else {
                shouldShowFilteredPlaceholder = true;
              }
            }
          }

          gridView.$('tbody, .tbody').hide();
          gridView.$('.aggregate-row').hide();

          if (_.isArray(placeholderConfig.footerVisableElement)) {
            gridView.$('tfoot, .tfoot').children().hide();
            _.each(placeholderConfig.footerVisableElement, (selector) => {
              gridView.$('tfoot, .tfoot').find(selector).show();
            });
          } else {
            gridView.$('tfoot, .tfoot').hide();
          }

          if (shouldShowFilteredPlaceholder) {
            options.filteredEmptyText = filteredEmptyText;
            options.hasFilter = true;
          }

          if (filteredContainer && shouldShowFilteredPlaceholder) {
          // legacy support for using a separate filteredContainer
            hide(container);
            applyTemplate({ options, element: filteredContainer });
          } else {
            hide(filteredContainer);
            applyTemplate({ options });
          }

          if (_.isFunction(placeholderConfig.asyncCallback)) {
            placeholderConfig.asyncCallback(container).then((shouldShow) => {
              if (shouldShow) {
                options.asyncEmptyText = asyncEmptyText;
                hide(filteredContainer);
                applyTemplate({ options });
                _.result(placeholderConfig, 'postAsyncAction');
              }
            });
          }
        } else {
        // show grids here since grids may trigger 'didUpdate' serveral times
        // and hide the grids after willUpdate.
          gridView.$('tbody, .tbody').show();
          gridView.$('tfoot, .tfoot').show();
          gridView.$('.aggregate-row').show();
          hide(container);
          hide(filteredContainer);
        }
      }
    });

    gridView.on('willUpdate', () => {
    // need to show this before redraw to ensure correctness of computation in virtualization mode.
      if (!placeholderConfig.skipRedraw) {
        gridView.$('tbody, .tbody').show();
        gridView.$('tfoot, .tfoot').show();
      }
    });
  }
);
